import React, { useState, useRef, useEffect } from 'react';
import {
  Card, Button, Space, Input, message, Alert, Divider, Tag, Row, Col, Badge, Collapse
} from 'antd';
import {
  PlayCircleOutlined, ClearOutlined, DownloadOutlined,
  InfoCircleOutlined, ReloadOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
// 按需引入 ECharts 组件以减少打包体积
import * as echarts from 'echarts/core';
import { GraphChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsOption } from 'echarts';

// Import watermark function
import { addWatermarkToImage } from '../common/Watermark';

// 注册必需的组件
echarts.use([
  GraphChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer
]);

const { TextArea } = Input;

// 接口定义
interface ParsedNode {
  id: string; // 唯一ID（地址_层级_索引）
  address: string; // 实际地址
  label: string; // 显示标签
  type: 'source' | 'target' | 'relay';
  layer: number; // 预分配的层级
}

interface ParsedEdge {
  source: string; // 源节点唯一ID
  target: string; // 目标节点唯一ID
  amount?: string; // 可选的金额信息
  hash?: string; // 可选的交易hash
}

interface ParseResult {
  nodes: ParsedNode[];
  edges: ParsedEdge[];
  errors: string[];
}

interface ValidationError {
  line: number;
  message: string;
}

const FundFlowVisualizer: React.FC = () => {
  const [sourceAddress, setSourceAddress] = useState('');
  const [targetAddress, setTargetAddress] = useState('');
  const [mappings, setMappings] = useState('');
  const [parseErrors, setParseErrors] = useState<ValidationError[]>([]);
  const [graphData, setGraphData] = useState<{ nodes: any[]; edges: any[] } | null>(null);
  const chartRef = useRef<ReactECharts>(null);

  // 地址格式校验
  const validateAddress = (address: string): boolean => {
    const trimmed = address.trim();
    // 基本校验：0x开头 + 至少6个十六进制字符
    return /^0x[0-9a-fA-F]{6,}$/.test(trimmed);
  };

  // 解析地址（支持带标签的格式，如 "Bitget 3(0x111...)"）
  const parseAddressWithLabel = (input: string): { address: string; label: string; displayName: string } | null => {
    const trimmed = input.trim();
    if (!trimmed) return null;

    // 尝试匹配 "名称(0x地址)" 格式
    const withParenMatch = trimmed.match(/^(.+?)\s*\(\s*(0x[0-9a-fA-F]{6,})\s*\)$/i);

    if (withParenMatch) {
      const namepart = withParenMatch[1].trim();
      const addressPart = withParenMatch[2].toLowerCase();

      // 校验地址部分
      if (validateAddress(addressPart)) {
        return {
          address: addressPart,
          label: shortenAddress(addressPart),
          displayName: namepart // 使用名称作为显示
        };
      }
    }

    // 尝试直接解析为地址
    const lowerInput = trimmed.toLowerCase();
    if (validateAddress(lowerInput)) {
      return {
        address: lowerInput,
        label: shortenAddress(lowerInput),
        displayName: shortenAddress(lowerInput)
      };
    }

    return null;
  };

  // 缩短地址显示
  const shortenAddress = (address: string): string => {
    if (address.length <= 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // 解析映射关系（支持两种格式）
  const parseMappings = (): ParseResult => {
    const errors: string[] = [];
    const nodes: ParsedNode[] = [];
    const edges: ParsedEdge[] = [];

    // 节点计数器（用于生成唯一ID）
    let nodeCounter = 0;
    const createNodeId = () => `node_${nodeCounter++}`;

    // 记录源和目标节点ID
    let sourceNodeId: string | null = null;
    let targetNodeId: string | null = null;

    // 1. 处理分发地址（支持带标签格式）
    let sourceAddr = '';
    let sourceDisplayName = '';
    if (sourceAddress.trim()) {
      const parsed = parseAddressWithLabel(sourceAddress.trim());
      if (!parsed) {
        errors.push('分发地址格式不正确');
      } else {
        sourceAddr = parsed.address;
        sourceDisplayName = parsed.displayName;
        sourceNodeId = createNodeId();
        nodes.push({
          id: sourceNodeId,
          address: sourceAddr,
          label: sourceDisplayName,
          type: 'source',
          layer: 0
        });
      }
    }

    // 2. 处理归集地址（支持带标签格式）
    let targetAddr = '';
    let targetDisplayName = '';
    if (targetAddress.trim()) {
      const parsed = parseAddressWithLabel(targetAddress.trim());
      if (!parsed) {
        errors.push('归集地址格式不正确');
      } else {
        targetAddr = parsed.address;
        targetDisplayName = parsed.displayName;

        // 检查是否和源地址相同
        if (targetAddr === sourceAddr) {
          // 源和目标相同，创建两个节点
          targetNodeId = createNodeId();
          nodes.push({
            id: targetNodeId,
            address: targetAddr,
            label: targetDisplayName,
            type: 'target',
            layer: 4
          });
        } else {
          targetNodeId = createNodeId();
          nodes.push({
            id: targetNodeId,
            address: targetAddr,
            label: targetDisplayName,
            type: 'target',
            layer: 4
          });
        }
      }
    }

    // 3. 解析映射行（支持两种格式）
    const lines = mappings.split('\n');
    const validationErrors: ValidationError[] = [];

    // 用于记录每行的节点ID（仅用于3地址格式）
    const lineNodes: string[][] = [];

    // 用于记录地址到节点的映射（用于资金流格式）
    const addressToNodes = new Map<string, string[]>();

    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // 验证必须包含恰好2个 '----' 分隔符（产生3个部分）
      const separatorCount = (trimmedLine.match(/----/g) || []).length;
      if (separatorCount !== 2) {
        validationErrors.push({
          line: lineIndex + 1,
          message: '格式错误：必须包含2个 \'----\' 分隔符'
        });
        return;
      }

      // 分割为3个部分
      const parts = trimmedLine.split('----').map(p => p.trim());

      if (parts.length !== 3) {
        validationErrors.push({
          line: lineIndex + 1,
          message: '格式错误：必须恰好有3个部分'
        });
        return;
      }

      const [part1, part2, part3] = parts;

      // 检测格式：如果中间部分包含"BNB"，则是资金流格式
      const isFlowFormat = part2.toUpperCase().includes('BNB');

      if (isFlowFormat) {
        // 资金流格式：from----amountBNB----to
        const fromAddr = part1.toLowerCase();
        const amount = part2;
        const toAddr = part3.toLowerCase();

        // 验证地址格式
        if (!validateAddress(fromAddr)) {
          validationErrors.push({
            line: lineIndex + 1,
            message: `源地址格式不合法: ${part1}`
          });
          return;
        }

        if (!validateAddress(toAddr)) {
          validationErrors.push({
            line: lineIndex + 1,
            message: `目标地址格式不合法: ${part3}`
          });
          return;
        }

        // 创建或获取节点
        let fromNodeId: string;
        let toNodeId: string;

        // 检查是否是预定义的源地址
        if (sourceAddr && fromAddr === sourceAddr) {
          fromNodeId = sourceNodeId!;
        } else {
          // 创建新节点（自动分配到Layer 2）
          fromNodeId = createNodeId();
          nodes.push({
            id: fromNodeId,
            address: fromAddr,
            label: shortenAddress(fromAddr),
            type: 'relay',
            layer: 2
          });
          // 记录地址映射
          if (!addressToNodes.has(fromAddr)) {
            addressToNodes.set(fromAddr, []);
          }
          addressToNodes.get(fromAddr)!.push(fromNodeId);
        }

        // 检查是否是预定义的目标地址
        if (targetAddr && toAddr === targetAddr) {
          toNodeId = targetNodeId!;
        } else {
          // 创建新节点（自动分配到Layer 2）
          toNodeId = createNodeId();
          nodes.push({
            id: toNodeId,
            address: toAddr,
            label: shortenAddress(toAddr),
            type: 'relay',
            layer: 2
          });
          // 记录地址映射
          if (!addressToNodes.has(toAddr)) {
            addressToNodes.set(toAddr, []);
          }
          addressToNodes.get(toAddr)!.push(toNodeId);
        }

        // 创建边（带金额信息）
        edges.push({
          source: fromNodeId,
          target: toNodeId,
          amount: amount
        });

      } else {
        // 原有的3地址固定层级格式
        const [addr1, addr2, addr3] = parts.map(p => p.toLowerCase());

        // 验证中间位置（币安Web3钱包）必须存在且有效
        if (!addr2 || !validateAddress(addr2)) {
          validationErrors.push({
            line: lineIndex + 1,
            message: '币安Web3钱包地址（中间位置）必须存在且格式正确'
          });
          return;
        }

        // 验证非空地址的格式
        if (addr1 && !validateAddress(addr1)) {
          validationErrors.push({
            line: lineIndex + 1,
            message: `第一个地址格式不合法: ${addr1}`
          });
          return;
        }

        if (addr3 && !validateAddress(addr3)) {
          validationErrors.push({
            line: lineIndex + 1,
            message: `第三个地址格式不合法: ${addr3}`
          });
          return;
        }

        // 为这一行创建节点
        const currentLineNodes: string[] = [];

        // 处理第一个地址（Layer 1）
        if (addr1) {
          if (sourceAddr && addr1 === sourceAddr) {
            currentLineNodes.push(sourceNodeId!);
          } else {
            const nodeId = createNodeId();
            nodes.push({
              id: nodeId,
              address: addr1,
              label: shortenAddress(addr1),
              type: 'relay',
              layer: 1
            });
            currentLineNodes.push(nodeId);
          }
        }

        // 处理第二个地址（Layer 2，币安Web3钱包）- 必定存在
        const nodeId2 = createNodeId();
        nodes.push({
          id: nodeId2,
          address: addr2,
          label: shortenAddress(addr2),
          type: 'relay',
          layer: 2
        });
        currentLineNodes.push(nodeId2);

        // 处理第三个地址（Layer 3）
        if (addr3) {
          if (targetAddr && addr3 === targetAddr) {
            currentLineNodes.push(targetNodeId!);
          } else {
            const nodeId = createNodeId();
            nodes.push({
              id: nodeId,
              address: addr3,
              label: shortenAddress(addr3),
              type: 'relay',
              layer: 3
            });
            currentLineNodes.push(nodeId);
          }
        }

        lineNodes.push(currentLineNodes);

        // 创建这一行内部的边
        for (let i = 0; i < currentLineNodes.length - 1; i++) {
          edges.push({
            source: currentLineNodes[i],
            target: currentLineNodes[i + 1]
          });
        }
      }
    });

    // 4. 自动生成：源地址 → 每行的第一个节点（仅用于3地址格式）
    if (sourceNodeId) {
      lineNodes.forEach(lineNodeIds => {
        if (lineNodeIds.length > 0) {
          const firstNodeId = lineNodeIds[0];
          if (firstNodeId !== sourceNodeId) {
            edges.push({
              source: sourceNodeId,
              target: firstNodeId
            });
          }
        }
      });
    }

    // 5. 自动生成：每行的最后一个节点 → 目标地址（仅用于3地址格式）
    if (targetNodeId) {
      lineNodes.forEach(lineNodeIds => {
        if (lineNodeIds.length > 0) {
          const lastNodeId = lineNodeIds[lineNodeIds.length - 1];
          if (lastNodeId !== targetNodeId) {
            edges.push({
              source: lastNodeId,
              target: targetNodeId
            });
          }
        }
      });
    }

    setParseErrors(validationErrors);

    return {
      nodes,
      edges,
      errors: validationErrors.map(e => `第 ${e.line} 行: ${e.message}`)
    };
  };

  // 计算节点层级（简化版：直接使用预分配的层级）
  const calculateNodeLevels = (result: ParseResult): Map<string, number> => {
    const levels = new Map<string, number>();

    result.nodes.forEach(node => {
      levels.set(node.id, node.layer);
    });

    return levels;
  };

  // 生成图表
  const handleGenerate = () => {
    if (!sourceAddress && !targetAddress && !mappings.trim()) {
      message.warning('请至少输入一项数据');
      return;
    }

    const result = parseMappings();

    if (result.errors.length > 0) {
      message.error('解析失败，请修正错误后重试');
      return;
    }

    if (result.nodes.length === 0) {
      message.warning('未解析到有效节点');
      return;
    }

    // 计算节点层级（固定5层：0-4）
    const nodeLevels = calculateNodeLevels(result);
    const maxLevel = 4; // 固定5层布局

    // 按层级分组节点
    const levelGroups = new Map<number, ParsedNode[]>();
    for (let i = 0; i <= maxLevel; i++) {
      levelGroups.set(i, []);
    }
    result.nodes.forEach(node => {
      const level = nodeLevels.get(node.id) || 2; // 默认放在中间层
      levelGroups.get(level)!.push(node);
    });

    // 计算节点位置（固定5层布局，大幅增加水平间距）
    const canvasWidth = 3600; // 显著增大画布宽度（约1.8倍）
    const canvasHeight = 800;
    const levelWidth = 550; // 固定层级间距（显著增大，约1.7倍）
    const marginLeft = 150; // 左边距

    // 构建 ECharts 图数据
    const nodes = result.nodes.map(node => {
      const level = node.layer;
      const nodesInLevel = levelGroups.get(level) || [];
      const indexInLevel = nodesInLevel.findIndex(n => n.id === node.id);

      // 节点间距
      const nodeSpacing = 70;

      // 计算该层节点组的总高度
      const totalGroupHeight = Math.max(nodesInLevel.length - 1, 0) * nodeSpacing;

      // 计算起始Y坐标，使节点组垂直居中
      const startY = (canvasHeight - totalGroupHeight) / 2;

      // 计算当前节点的Y坐标
      const y = startY + indexInLevel * nodeSpacing;

      // 计算 x 坐标
      const x = marginLeft + level * levelWidth;

      // 根据层级确定颜色和大小
      let color = '#8c8c8c';
      let symbolSize = 35; // 默认节点大小（缩小）

      if (level === 0) {
        // Layer 0: 源地址
        color = '#1890ff'; // 蓝色
        symbolSize = 50; // 源地址稍大
      } else if (level === 1) {
        // Layer 1: 第一批中转
        color = '#597ef7'; // 浅蓝
        symbolSize = 40;
      } else if (level === 2) {
        // Layer 2: 核心交互地址
        color = '#faad14'; // 橙色
        symbolSize = 42;
      } else if (level === 3) {
        // Layer 3: 第二批中转
        color = '#95de64'; // 浅绿
        symbolSize = 40;
      } else if (level === 4) {
        // Layer 4: 目标地址
        color = '#52c41a'; // 绿色
        symbolSize = 50; // 目标地址稍大
      }

      // 明确标记的source和target优先
      if (node.type === 'source') {
        color = '#1890ff';
        symbolSize = 50;
      } else if (node.type === 'target') {
        color = '#52c41a';
        symbolSize = 50;
      }

      return {
        id: node.id, // 使用唯一ID
        name: node.label, // 显示缩短的地址
        x: x,
        y: y,
        symbolSize: symbolSize,
        itemStyle: {
          color: color,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: true,
          position: 'right',
          fontSize: 10, // 稍微缩小字体
          color: '#333',
          fontWeight: level === 0 || level === 4 ? 'bold' : 'normal'
        },
        tooltip: {
          formatter: () => {
            const layerNames = ['分发地址(资金来源)', '第一批中转', '币安Web3钱包', '第二批中转', '归集地址(资金去向)'];
            const layerText = layerNames[level] || '未知层';
            const inEdges = result.edges.filter(e => e.target === node.id).length;
            const outEdges = result.edges.filter(e => e.source === node.id).length;
            return `<strong>${layerText}</strong><br/>
                    完整地址: ${node.address}<br/>
                    层级: Layer ${level}<br/>
                    流入边数: ${inEdges}<br/>
                    流出边数: ${outEdges}`;
          }
        },
        category: level,
        fixed: true // 固定节点位置
      };
    });

    const links = result.edges.map(edge => {
      // 获取源节点和目标节点
      const sourceNode = result.nodes.find(n => n.id === edge.source);
      const targetNode = result.nodes.find(n => n.id === edge.target);

      const sourceLevel = sourceNode?.layer || 2;
      const targetLevel = targetNode?.layer || 2;

      // 根据层级差异确定边的颜色
      let color = '#8c8c8c';
      if (sourceLevel === 0) {
        color = '#1890ff'; // 从源地址出发：蓝色
      } else if (targetLevel === 4) {
        color = '#52c41a'; // 到达目标地址：绿色
      } else if (sourceLevel < targetLevel) {
        color = '#91d5ff'; // 正向流动：浅蓝
      }

      // 构建边对象
      const link: any = {
        source: edge.source,
        target: edge.target,
        lineStyle: {
          color: color,
          width: edge.amount ? 2 : 1.5, // 有金额信息的边稍粗
          curveness: 0.1
        }
      };

      // 如果有金额信息，添加标签和tooltip
      if (edge.amount) {
        link.label = {
          show: true,
          formatter: edge.amount,
          fontSize: 10,
          color: '#ff4d4f',
          fontWeight: 'bold'
        };
        link.tooltip = {
          formatter: () => {
            return `<strong>资金流动</strong><br/>
                    从: ${sourceNode?.address || '未知'}<br/>
                    到: ${targetNode?.address || '未知'}<br/>
                    金额: <strong style="color: #ff4d4f">${edge.amount}</strong>${edge.hash ? `<br/>交易Hash: ${edge.hash}` : ''}`;
          }
        };
      }

      return link;
    });

    setGraphData({ nodes, edges: links });

    // 统计各层节点数量
    const layerStats = Array.from(levelGroups.entries())
      .filter(([_, nodes]) => nodes.length > 0)
      .map(([layer, nodes]) => `Layer ${layer}: ${nodes.length}个`)
      .join(', ');

    message.success(`成功解析 ${nodes.length} 个节点，${links.length} 条边 (${layerStats})`);
  };

  // 清空输入
  const handleClear = () => {
    setSourceAddress('');
    setTargetAddress('');
    setMappings('');
    setParseErrors([]);
    setGraphData(null);
  };

  // 导出图片
  const handleExport = async () => {
    if (!chartRef.current) {
      message.error('图表未生成');
      return;
    }

    try {
      const chartInstance = chartRef.current.getEchartsInstance();
      const url = chartInstance.getDataURL({
        pixelRatio: 2,
        backgroundColor: '#fff',
        type: 'png'
      });

      // Add watermark to the image
      const watermarkedUrl = await addWatermarkToImage(url, '0xluoye', 20, 120, 0.15);

      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `fund-flow-${timestamp}.png`;
      link.href = watermarkedUrl;
      link.click();

      message.success('图片导出成功');
    } catch (error) {
      console.error('Export error:', error);
      message.error('导出失败');
    }
  };

  // 重置视图
  const handleResetView = () => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      chartInstance.dispatchAction({
        type: 'restore'
      });
      message.success('视图已重置');
    }
  };

  // ECharts 配置
  const getChartOption = (): EChartsOption => {
    if (!graphData) return {};

    return {
      title: {
        text: '资金流动示意图',
        subtext: 'Layer 0: 分发地址 → Layer 1: 中转1 → Layer 2: 币安Web3钱包 → Layer 3: 中转2 → Layer 4: 归集地址',
        left: 'center',
        top: 10,
        subtextStyle: {
          fontSize: 12,
          color: '#666'
        }
      },
      tooltip: {
        trigger: 'item'
      },
      legend: [
        {
          data: [
            { name: '0', itemStyle: { color: '#1890ff' } },
            { name: '1', itemStyle: { color: '#597ef7' } },
            { name: '2', itemStyle: { color: '#faad14' } },
            { name: '3', itemStyle: { color: '#95de64' } },
            { name: '4', itemStyle: { color: '#52c41a' } }
          ],
          orient: 'vertical',
          left: 'left',
          top: 70,
          formatter: (name: string) => {
            const layerNames = ['Layer 0: 分发地址', 'Layer 1: 中转1', 'Layer 2: 币安Web3钱包', 'Layer 3: 中转2', 'Layer 4: 归集地址'];
            return layerNames[parseInt(name)] || name;
          }
        }
      ],
      series: [
        {
          type: 'graph',
          layout: 'none', // 不使用力导向，使用固定坐标
          data: graphData.nodes,
          links: graphData.edges,
          categories: [
            { name: '0', itemStyle: { color: '#1890ff' } },
            { name: '1', itemStyle: { color: '#597ef7' } },
            { name: '2', itemStyle: { color: '#faad14' } },
            { name: '3', itemStyle: { color: '#95de64' } },
            { name: '4', itemStyle: { color: '#52c41a' } }
          ],
          roam: true, // 允许缩放和平移
          draggable: false, // 禁止拖拽节点（因为是固定布局）
          label: {
            show: true,
            position: 'right'
          },
          emphasis: {
            focus: 'adjacency',
            lineStyle: {
              width: 3 // 高亮时稍微粗一点
            }
          },
          edgeSymbol: ['none', 'arrow'],
          edgeSymbolSize: [0, 8], // 稍微小一点的箭头
          lineStyle: {
            opacity: 0.9,
            curveness: 0.1
          }
        }
      ]
    };
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="资金流动可视化" bordered={false}>
        {/* 可折叠的使用说明 */}
        <Collapse
          ghost
          items={[
            {
              key: '1',
              label: <span style={{ color: '#1890ff', fontWeight: 500 }}><InfoCircleOutlined /> 使用说明（点击展开）</span>,
              children: (
                <div style={{ paddingLeft: 24 }}>
                  <p><strong>支持两种输入格式：</strong></p>

                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontWeight: 600, color: '#52c41a' }}>格式1：资金流格式（推荐）</p>
                    <p style={{ marginLeft: '1em' }}>
                      用于直接展示资金流动路径，每行格式：<code>from地址----金额BNB----to地址</code>
                    </p>
                    <p style={{ marginLeft: '1em', fontFamily: 'monospace', background: '#f0f5ff', padding: '8px', borderRadius: '4px' }}>
                      示例：<br/>
                      0xAAAA....----3.01BNB----0xBBBB....<br/>
                      0xBBBB....----2.50BNB----0xCCCC....<br/>
                    </p>
                    <p style={{ marginLeft: '1em', color: '#666', fontSize: 12 }}>
                      ✨ 金额会直接显示在连线上，鼠标悬停可查看完整信息<br/>
                      ✨ 可与币安Web3钱包检测工具配合使用：检测完成后点击"复制资金流格式"即可
                    </p>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontWeight: 600, color: '#faad14' }}>格式2：固定5层布局格式</p>
                    <p style={{ marginLeft: '1em' }}>
                      <Tag color="blue">Layer 0: 分发地址(资金来源)</Tag> →
                      <Tag color="geekblue">Layer 1: 第一批中转</Tag> →
                      <Tag color="orange">Layer 2: 币安Web3钱包</Tag> →
                      <Tag color="lime">Layer 3: 第二批中转</Tag> →
                      <Tag color="green">Layer 4: 归集地址(资金去向)</Tag>
                    </p>
                    <p style={{ marginLeft: '1em' }}>
                      每行格式：<code>中转1----币安Web3----中转2</code>（必须包含2个<code>----</code>分隔符）
                    </p>
                  </div>

                  <Divider style={{ margin: '12px 0' }} />
                  <p><strong>关键说明：</strong></p>
                  <p>1. <strong>分发地址</strong>和<strong>归集地址</strong>支持两种格式：</p>
                  <p style={{ marginLeft: '1em' }}>
                    - 直接地址：<code>0xAAAA...</code><br/>
                    - 带标签：<code>Bitget 3(0xAAAA...)</code> 或 <code>CEX 1(0xXXXX...)</code>
                  </p>
                  <p>2. 系统会自动识别输入格式（根据中间部分是否包含"BNB"）</p>
                  <p>3. <strong>资金流格式</strong>：直接输入关系映射即可，分发/归集地址可选</p>
                  <p>4. <strong>固定层级格式</strong>：</p>
                  <p style={{ marginLeft: '1em' }}>
                    - 中间位置（币安Web3钱包）必须存在<br/>
                    - 第一个和第三个位置可以为空（如：<code>----0xWeb3----0xRelay</code>）<br/>
                    - 分发地址会自动连接到每行的第一个非空地址<br/>
                    - 每行的最后一个非空地址会自动连接到归集地址
                  </p>
                </div>
              )
            }
          ]}
          style={{ marginBottom: 16 }}
        />

        {/* 输入区域 */}
        <Card size="small" style={{ marginBottom: 24, backgroundColor: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  分发地址（资金来源）
                </label>
                <Input
                  placeholder="例如: 0xAAAA... 或 Bitget 3(0xAAAA...)"
                  value={sourceAddress}
                  onChange={(e) => setSourceAddress(e.target.value)}
                  status={sourceAddress && !parseAddressWithLabel(sourceAddress.trim()) ? 'error' : ''}
                />
                {sourceAddress && !parseAddressWithLabel(sourceAddress.trim()) && (
                  <span style={{ color: '#ff4d4f', fontSize: 12 }}>格式不正确</span>
                )}
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  归集地址（资金去向）
                </label>
                <Input
                  placeholder="例如: 0xXXXX... 或 CEX 1(0xXXXX...)"
                  value={targetAddress}
                  onChange={(e) => setTargetAddress(e.target.value)}
                  status={targetAddress && !parseAddressWithLabel(targetAddress.trim()) ? 'error' : ''}
                />
                {targetAddress && !parseAddressWithLabel(targetAddress.trim()) && (
                  <span style={{ color: '#ff4d4f', fontSize: 12 }}>格式不正确</span>
                )}
              </div>
            </Col>

            <Col xs={24} md={8}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  操作
                </label>
                <Space>
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={handleGenerate}
                  >
                    生成图表
                  </Button>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClear}
                  >
                    清空
                  </Button>
                  {graphData && (
                    <>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={handleExport}
                      >
                        导出
                      </Button>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleResetView}
                      >
                        重置
                      </Button>
                    </>
                  )}
                </Space>
              </div>
            </Col>
          </Row>

          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  关系映射（支持两种格式）
                </label>
                <TextArea
                  placeholder={`支持两种格式自动识别：
格式1（资金流，推荐）：
0xAAAA----3.01BNB----0xBBBB
0xBBBB----2.50BNB----0xCCCC

格式2（固定层级）：
0xBBBB----0xCCCC----0xDDDD
----0xEEEE----0xFFFF`}
                  value={mappings}
                  onChange={(e) => setMappings(e.target.value)}
                  rows={8}
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
            </Col>
          </Row>

          {parseErrors.length > 0 && (
            <Alert
              message="解析错误"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {parseErrors.map((err, idx) => (
                    <li key={idx}>第 {err.line} 行: {err.message}</li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Card>

        {/* 图表展示区 */}
        <div style={{
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          padding: 16,
          minHeight: 700,
          backgroundColor: '#fafafa',
          overflow: 'auto'
        }}>
          {graphData ? (
            <ReactECharts
              ref={chartRef}
              echarts={echarts}
              option={getChartOption()}
              style={{ height: '680px', minWidth: '100%' }}
              opts={{ renderer: 'canvas' }}
            />
          ) : (
            <div style={{
              height: '680px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#8c8c8c'
            }}>
              请输入数据并点击"生成图表"按钮
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default FundFlowVisualizer;
